import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ALL_DISH_CATEGORIES, CatalogSource, DishCatalogDto, DishType } from '@wellverse/shared-ts';
import { ExcelButtons } from '../../components/ExcelButtons';

export const DishCatalogList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<DishType[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<CatalogSource[]>([]);

  const { tableProps } = useTable<DishCatalogDto>({
    resource: 'dish-catalog',
    filters: {
      permanent: [
        { field: 'search', operator: 'contains' as const, value: searchText },
        ...(selectedTypes.length
          ? [{ field: 'type', operator: 'in' as const, value: selectedTypes }]
          : []),
        ...(selectedCategories.length
          ? [{ field: 'category', operator: 'in' as const, value: selectedCategories }]
          : []),
        ...(selectedSources.length
          ? [{ field: 'source', operator: 'in' as const, value: selectedSources }]
          : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="dish-catalog">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search dishes by name or alias..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <Select
          mode="multiple"
          placeholder="Filter by type"
          allowClear
          maxTagCount="responsive"
          style={{ minWidth: 180 }}
          value={selectedTypes}
          onChange={(value) => setSelectedTypes(value)}
          options={Object.values(DishType).map((t) => ({ label: t, value: t }))}
        />
        <Select
          mode="multiple"
          placeholder="Filter by category"
          allowClear
          showSearch
          maxTagCount="responsive"
          style={{ minWidth: 220 }}
          value={selectedCategories}
          onChange={(value) => setSelectedCategories(value)}
          options={ALL_DISH_CATEGORIES.map((c) => ({
            label: c.replace(/_/g, ' '),
            value: c,
          }))}
        />
        <Select
          mode="multiple"
          placeholder="Filter by source"
          allowClear
          maxTagCount="responsive"
          style={{ minWidth: 180 }}
          value={selectedSources}
          onChange={(value) => setSelectedSources(value)}
          options={Object.values(CatalogSource).map((s) => ({ label: s, value: s }))}
        />
        <ExcelButtons resource="dish-catalog" />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1300 }}>
        <Table.Column dataIndex="name" title="Name" fixed="left" width={200} />
        <Table.Column
          title="Translations"
          width={160}
          render={(_: any, record: DishCatalogDto) => {
            const t = record.translations;
            if (!t || Object.keys(t).length === 0) return '—';
            return (
              <Space wrap size={4}>
                {Object.entries(t).map(([locale, data]) => (
                  <Tag key={locale} style={{ borderRadius: 9999, padding: '2px 8px', fontSize: 12 }}>
                    {locale}: {data.name}
                  </Tag>
                ))}
              </Space>
            );
          }}
        />
        <Table.Column
          title="Aliases"
          width={180}
          render={(_: any, record: DishCatalogDto) => (
            <Space wrap size={4}>
              {(record.aliases || []).slice(0, 3).map((a) => (
                <Tag key={a} style={{ borderRadius: 9999, padding: '2px 8px', fontSize: 12 }}>{a}</Tag>
              ))}
              {(record.aliases || []).length > 3 && (
                <Tag style={{ borderRadius: 9999, padding: '2px 8px', fontSize: 12 }}>+{(record.aliases || []).length - 3}</Tag>
              )}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="type"
          title="Type"
          width={100}
          render={(type: string) => type || '—'}
        />
        <Table.Column dataIndex="category" title="Category" width={120} />
        <Table.Column
          dataIndex="source"
          title="Source"
          width={100}
          render={(source: string) => {
            const colors: Record<string, string> = {
              USDA: '#52c41a',
              CFS: '#1890ff',
              MANUAL: '#d9d9d9',
              AI_GENERATED: '#722ed1',
            };
            const c = colors[source] || '#898989';
            return (
              <Tag style={{ borderColor: c, color: c, borderRadius: 9999, padding: '2px 12px', fontWeight: 500 }}>
                {source}
              </Tag>
            );
          }}
        />
        <Table.Column dataIndex="standardPortionGrams" title="Portion (g)" width={100} />
        <Table.Column dataIndex="calories" title="Calories" width={100} />
        <Table.Column dataIndex="protein" title="Protein (g)" width={110} />
        <Table.Column dataIndex="fat" title="Fat (g)" width={90} />
        <Table.Column dataIndex="carbs" title="Carbs (g)" width={100} />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          width={90}
          filters={[
            { text: 'Active', value: true },
            { text: 'Inactive', value: false },
          ]}
          onFilter={(value, record: any) => record.isActive === value}
          render={(isActive: boolean) => (
            <Tag
              style={{
                borderColor: isActive ? '#3ecf8e' : '#898989',
                color: isActive ? '#3ecf8e' : '#898989',
                background: isActive ? '#3ecf8e15' : '#89898915',
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          fixed="right"
          width={120}
          render={(_: any, record: DishCatalogDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="dish-catalog" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="dish-catalog" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="dish-catalog"
                confirmTitle="Permanently delete this dish?"
                confirmOkText="Delete permanently" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
